import { AppNavigationsSettings } from '@common-modules/navigation/models/app-navigations-settings';
import { MenuLinkConf } from '@common-modules/navigation/models/menu-link-conf';
import { ModuleLinkConf } from '@common-modules/navigation/models/module-link-conf';
import { AppModules } from '@common-modules/shared/app-modules.enum';
import { RouteNameHelper } from '@water-loss/features/navigation/route-name-helper';
import { WlmNavKeys } from './features/navigation/wlm-nav-keys';
import { waterLossAppPermissions } from './water-loss-permissions';

const T_SCOPE = `${AppModules.WlmNavigation}.wlm-navigation-service`;

const waterLossRoutePermissions = waterLossAppPermissions.routePermissions;

export const waterLossAppNavigationPaths = {
  [WlmNavKeys.Integration]: '/integration',
};

export const waterLossAppNavigations: AppNavigationsSettings = {
  moduleLinkKeys: [
    WlmNavKeys.LeakageCalculationModule,
    WlmNavKeys.PressureManagementModule,
    WlmNavKeys.AlcModule,
    WlmNavKeys.WaterBalanceModule,
    WlmNavKeys.BiModule,
    WlmNavKeys.DataAccessModule,
    WlmNavKeys.MonitoringModule,
    WlmNavKeys.ConfigurationModule,
    WlmNavKeys.AdministrationModule,
    WlmNavKeys.LogsModule,
    WlmNavKeys.ValidationModule,
  ],

  hiddenLinks: new Map<WlmNavKeys, MenuLinkConf>([
    [
      WlmNavKeys.AlarmsCreationTelemetry,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.telemetry`,
        urlPath: '/configuration/alarm/create/telemetry',
        svgIcon: 'telemetry_point',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.AlarmsCreationTelemetry),
      }),
    ],
    [
      WlmNavKeys.AlarmsCreationAlgorithms,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.algorithms`,
        urlPath: '/configuration/alarm/create/algorithms',
        svgIcon: 'algorithms',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.AlarmsCreationAlgorithms),
      }),
    ],
    [
      WlmNavKeys.NetworkElementDetails,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.ne-config`,
        urlPath: `/configuration/${RouteNameHelper.neConfigDetail}`,
        svgIcon: 'network',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.NetworkElementDetails),
      }),
    ],
    [
      WlmNavKeys.BIHome,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.bi`,
        urlPath: `${RouteNameHelper.biDetails}`,
        svgIcon: 'business_intelligence',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.BIHome),
      }),
    ],
    [
      WlmNavKeys.Integration,
      new MenuLinkConf({
        urlPath: '/integration',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.Integration),
      }),
    ],
  ]),

  // Contains all the links and permissions needed for each route. Each route is identified by a nav key.
  links: new Map<WlmNavKeys, MenuLinkConf | ModuleLinkConf>([
    [
      WlmNavKeys.Home,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.home`,
        shortTitleTranslateKey: `${T_SCOPE}.home-short`,
        urlPath: '',
        svgIcon: 'home_group',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.Home),
        standalone: true,
      }),
    ],
    [
      WlmNavKeys.LeakageCalculationModule,
      new ModuleLinkConf({
        titleTranslateKey: `${T_SCOPE}.leakage-calculation`,
        shortTitleTranslateKey: `${T_SCOPE}.leakage-calculation-short`,
        svgIcon: 'leakage_calculation_group',
        menuLinksKeys: [WlmNavKeys.DistributionNetwork, WlmNavKeys.TransmissionNetwork],
        permissions: waterLossRoutePermissions.get(WlmNavKeys.LeakageCalculationModule),
      }),
    ],
    [
      WlmNavKeys.DistributionNetwork,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.dn`,
        shortTitleTranslateKey: `${T_SCOPE}.dn-short`,
        urlPath: '/dn/distribution-network/grid',
        svgIcon: 'distribution_network',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.DistributionNetwork),
      }),
    ],
    [
      WlmNavKeys.TransmissionNetwork,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.tn`,
        shortTitleTranslateKey: `${T_SCOPE}.tn-short`,
        urlPath: '/tn/transmission-network/grid',
        svgIcon: 'transmission-network',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.TransmissionNetwork),
      }),
    ],
    [
      WlmNavKeys.PressureManagementModule,
      new ModuleLinkConf({
        titleTranslateKey: `${T_SCOPE}.pressure-management`,
        shortTitleTranslateKey: `${T_SCOPE}.pressure-management-short`,
        menuLinksKeys: [WlmNavKeys.PressureMonitoring],
        svgIcon: 'pressure_management_group',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.PressureManagementModule),
      }),
    ],
    [
      WlmNavKeys.PressureMonitoring,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.pressure-monitoring`,
        urlPath: '/pressure-management/pressure-monitoring',
        svgIcon: 'pressure_monitoring',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.PressureMonitoring),
      }),
    ],
    [
      WlmNavKeys.AlcModule,
      new ModuleLinkConf({
        titleTranslateKey: `${T_SCOPE}.alc`,
        shortTitleTranslateKey: `${T_SCOPE}.alc-short`,
        svgIcon: 'active_leakage_control_group',
        menuLinksKeys: [
          WlmNavKeys.Prioritisation,
          WlmNavKeys.ActivityRaising,
          WlmNavKeys.Activities,
          WlmNavKeys.Leaks,
          WlmNavKeys.Campaigns,
        ],
        permissions: waterLossRoutePermissions.get(WlmNavKeys.AlcModule),
      }),
    ],
    [
      WlmNavKeys.Prioritisation,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.prioritisation`,
        urlPath: '/alc/prioritisation/grid',
        svgIcon: 'prioritisation',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.Prioritisation),
      }),
    ],
    [
      WlmNavKeys.Campaigns,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.campaigns`,
        urlPath: '/alc/campaigns/grid',
        svgIcon: 'campaign',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.Campaigns),
      }),
    ],
    [
      WlmNavKeys.ActivityRaising,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.activity-raising`,
        urlPath: '/alc/activity',
        svgIcon: 'activity_raising',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.ActivityRaising),
      }),
    ],
    [
      WlmNavKeys.Activities,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.activities`,
        urlPath: '/alc/activities/grid',
        svgIcon: 'activities',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.Activities),
      }),
    ],
    [
      WlmNavKeys.Leaks,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.leaks`,
        urlPath: '/alc/leaks/grid',
        svgIcon: 'leaks',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.Leaks),
      }),
    ],
    [
      WlmNavKeys.WaterBalanceModule,
      new ModuleLinkConf({
        titleTranslateKey: `${T_SCOPE}.water-balance`,
        shortTitleTranslateKey: `${T_SCOPE}.water-balance-short`,
        svgIcon: 'water_balance_group',
        menuLinksKeys: [WlmNavKeys.WaterBalance],
        permissions: waterLossRoutePermissions.get(WlmNavKeys.WaterBalanceModule),
      }),
    ],
    [
      WlmNavKeys.WaterBalance,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.water-balance`,
        urlPath: '/water-balance',
        svgIcon: 'water_balance',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.WaterBalance),
      }),
    ],
    [
      WlmNavKeys.BiModule,
      new ModuleLinkConf({
        titleTranslateKey: `${T_SCOPE}.bi`,
        shortTitleTranslateKey: `${T_SCOPE}.bi-short`,
        svgIcon: 'business_intelligence_group',
        menuLinksKeys: [WlmNavKeys.Bi],
        permissions: waterLossRoutePermissions.get(WlmNavKeys.BiModule),
      }),
    ],
    [
      WlmNavKeys.Bi,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.bi-dashboard`,
        urlPath: '/bi',
        svgIcon: 'business_intelligence',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.Bi),
      }),
    ],
    [
      WlmNavKeys.DataAccessModule,
      new ModuleLinkConf({
        titleTranslateKey: `${T_SCOPE}.data-access`,
        shortTitleTranslateKey: `${T_SCOPE}.data-access-short`,
        svgIcon: 'data_access_group',
        menuLinksKeys: [
          WlmNavKeys.Map,
          WlmNavKeys.DataValidation,
          WlmNavKeys.DataVisualization,
          WlmNavKeys.LeakageReporting,
          WlmNavKeys.CustomerDetails,
          WlmNavKeys.Schematics,
        ],
        permissions: waterLossRoutePermissions.get(WlmNavKeys.DataAccessModule),
      }),
    ],
    [
      WlmNavKeys.Map,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.map`,
        urlPath: '/map',
        svgIcon: 'layers',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.Map),
      }),
    ],
    [
      WlmNavKeys.DataValidation,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.data-validation`,
        urlPath: '/da/manual-data-validation',
        svgIcon: 'data_validation',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.DataValidation),
      }),
    ],
    [
      WlmNavKeys.DataVisualization,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.dv`,
        urlPath: '/dv/data-visualization',
        svgIcon: 'data_visualization',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.DataVisualization),
      }),
    ],
    [
      WlmNavKeys.LeakageReporting,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.leakage-reporting`,
        urlPath: '/dv/leakage-reporting/grid',
        svgIcon: 'leakage_reporting',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.LeakageReporting),
      }),
    ],
    [
      WlmNavKeys.CustomerDetails,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.customer-details`,
        urlPath: '/customer/detail',
        svgIcon: 'customer-details',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.CustomerDetails),
      }),
    ],
    [
      WlmNavKeys.Schematics,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.schematics`,
        urlPath: '/schematics',
        svgIcon: 'schematics',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.Schematics),
      }),
    ],
    [
      WlmNavKeys.MonitoringModule,
      new ModuleLinkConf({
        titleTranslateKey: `${T_SCOPE}.monitoring`,
        svgIcon: 'monitoring_group',
        shortTitleTranslateKey: `${T_SCOPE}.monitoring-short`,
        menuLinksKeys: [WlmNavKeys.Notifications],
        permissions: waterLossRoutePermissions.get(WlmNavKeys.MonitoringModule),
      }),
    ],
    [
      WlmNavKeys.Notifications,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.notifications`,
        urlPath: '/notifications',
        svgIcon: 'notifications',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.Notifications),
      }),
    ],
    [
      WlmNavKeys.ConfigurationModule,
      new ModuleLinkConf({
        titleTranslateKey: `${T_SCOPE}.configuration`,
        shortTitleTranslateKey: `${T_SCOPE}.configuration-short`,
        svgIcon: 'configuration',
        menuLinksKeys: [
          WlmNavKeys.TelemetryPoints,
          WlmNavKeys.AlarmsConfiguration,
          WlmNavKeys.NetworkElementConfig,
          WlmNavKeys.Algorithms,
          WlmNavKeys.Hierarchy,
          WlmNavKeys.UoM,
          WlmNavKeys.Calendar,
          WlmNavKeys.MergedZones,
        ],
        permissions: waterLossRoutePermissions.get(WlmNavKeys.ConfigurationModule),
      }),
    ],
    [
      WlmNavKeys.TelemetryPoints,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.telemetry-points`,
        urlPath: '/configuration/telemetry',
        svgIcon: 'telemetry_point',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.TelemetryPoints),
      }),
    ],
    [
      WlmNavKeys.AlarmsConfiguration,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.alarms-configuration`,
        urlPath: '/configuration/alarm',
        svgIcon: 'alarms',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.AlarmsConfiguration),
      }),
    ],
    [
      WlmNavKeys.NetworkElementConfig,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.ne-config`,
        urlPath: `/configuration/${RouteNameHelper.neConfig}`,
        svgIcon: 'network',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.NetworkElementConfig),
      }),
    ],
    [
      WlmNavKeys.Algorithms,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.algorithms-configuration`,
        urlPath: '/configuration/algorithm',
        svgIcon: 'algorithms',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.Algorithms),
      }),
    ],
    [
      WlmNavKeys.Hierarchy,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.hierarchy`,
        urlPath: '/configuration/hierarchy',
        svgIcon: 'hierarchy',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.Hierarchy),
      }),
    ],
    [
      WlmNavKeys.UoM,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.uom`,
        urlPath: `/configuration/${RouteNameHelper.uom}`,
        svgIcon: 'uom',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.UoM),
      }),
    ],
    [
      WlmNavKeys.Calendar,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.calendar`,
        urlPath: `/configuration/${RouteNameHelper.calendar}`,
        svgIcon: 'calendar',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.Calendar),
      }),
    ],
    [
      WlmNavKeys.MergedZones,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.merged-zones`,
        urlPath: `/configuration/${RouteNameHelper.mergedZones}`,
        svgIcon: 'merged-zone',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.MergedZones),
      }),
    ],
    [
      WlmNavKeys.AdministrationModule,
      new ModuleLinkConf({
        titleTranslateKey: `${T_SCOPE}.administration`,
        shortTitleTranslateKey: `${T_SCOPE}.administration-short`,
        svgIcon: 'administration_group',
        menuLinksKeys: [WlmNavKeys.Users, WlmNavKeys.Groups],
        permissions: waterLossRoutePermissions.get(WlmNavKeys.AdministrationModule),
      }),
    ],
    [
      WlmNavKeys.Users,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.users`,
        urlPath: '/roles/users',
        svgIcon: 'person',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.Users),
      }),
    ],
    [
      WlmNavKeys.Groups,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.groups`,
        urlPath: '/roles/groups',
        svgIcon: 'groups',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.Groups),
      }),
    ],
    [
      WlmNavKeys.LogsModule,
      new ModuleLinkConf({
        titleTranslateKey: `${T_SCOPE}.logs`,
        shortTitleTranslateKey: `${T_SCOPE}.logs-short`,
        svgIcon: 'logs',
        menuLinksKeys: [WlmNavKeys.LogsInterfaces, WlmNavKeys.LogsSystem, WlmNavKeys.Audits],
        permissions: waterLossRoutePermissions.get(WlmNavKeys.LogsModule),
      }),
    ],
    [
      WlmNavKeys.LogsInterfaces,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.logs-interfaces`,
        urlPath: `logs/${RouteNameHelper.logsInterfaces}`,
        svgIcon: 'interfaces',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.LogsInterfaces),
      }),
    ],
    [
      WlmNavKeys.LogsSystem,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.logs-system`,
        urlPath: `logs/${RouteNameHelper.logsSystem}`,
        svgIcon: 'system',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.LogsSystem),
      }),
    ],
    [
      WlmNavKeys.Audits,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.audits`,
        urlPath: `logs/${RouteNameHelper.audits}`,
        svgIcon: 'audits',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.Audits),
      }),
    ],
    [
      WlmNavKeys.ValidationModule,
      new ModuleLinkConf({
        titleTranslateKey: `${T_SCOPE}.validation`,
        shortTitleTranslateKey: `${T_SCOPE}.validation-short`,
        svgIcon: 'validation-module',
        menuLinksKeys: [WlmNavKeys.RuleSets, WlmNavKeys.VEGroups],
        permissions: waterLossRoutePermissions.get(WlmNavKeys.ValidationModule),
      }),
    ],
    [
      WlmNavKeys.RuleSets,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.rulesets`,
        urlPath: `validation/${RouteNameHelper.rulesets}`,
        svgIcon: 'rulesets',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.RuleSets),
      }),
    ],
    [
      WlmNavKeys.VEGroups,
      new MenuLinkConf({
        titleTranslateKey: `${T_SCOPE}.vegroups`,
        urlPath: `validation/${RouteNameHelper.vegroups}`,
        svgIcon: 've-groups',
        permissions: waterLossRoutePermissions.get(WlmNavKeys.VEGroups),
      }),
    ],
  ]),
};
